import { SymmetricDirectedGraphBuilder } from "../symmetric/symmetric-directed-graph-builder";
import { Node } from "../lib/node";
import { Edge } from "../lib/edge";
import { GraphAlgorithm } from "../graph-algorithm";


/**
 * This abstract class is a setup for creating an 
 * interactive symmetric directed graph 
 * which is responsible for creating graphs that serve as path-finding exercise.
 * We provide the framework to setup the source and targetnodes
 * + other additionally constraints.
 * We implemented the user-methods(leftclick, doubleclick ...) 
 * to control user interaction. 
 * 
 * Doubleclick for creating/delete a node.
 * Strg or shift + leftclick for connecting nodes.
 * 
 * 
 */
export abstract class FindPathSymDGBuilder extends SymmetricDirectedGraphBuilder {


    //Reference to the sourcenode
    protected sourceNode: Node;
    //Reference to the targetnode
    protected targetNode: Node;

    //Reference to the mandatory nodes
    protected mandatoryNodes: Node[] = [];

    //Modes to distinguish when selecting a special node
    protected selectSourceMode: boolean = false;
    protected selectTargetMode: boolean = false;
    protected selectMandatoryMode: boolean = false;

    //Colors of the special nodes
    protected sourceNodeColor: string = "green";
    protected targetNodeColor: string = "red";
    protected mandatoryNodeColor: string = "orange";


    protected cachedAllPaths: [Node[], Edge[]][] = [];
    protected cacheInvalidPaths: boolean = true;

    constructor(canvas: HTMLCanvasElement, rows: number, cols: number) {
        super(canvas, rows, cols);
        this.setGridVisible(false);

        this.sourceNode = null;
        this.targetNode = null;
        this.mandatoryNodes = [];
    }

    protected abstract computeAllPaths(): [Node[], Edge[]][];

    getSourceNodeAsNode(): Node {
        return this.sourceNode;
    }

    getTargetNodeAsNode(): Node {
        return this.targetNode;
    }

    getAllPaths(): [Node[], Edge[]][] {
        return this.cachedAllPaths;
    }

    setInvalidCache(invalid: boolean) {
        this.cacheInvalidPaths = invalid;
        this.update();
    }

    addMandatoryNode(node: Node) {
        if (this.mandatoryNodes.includes(node) || !this.nodes.includes(node)
            || node == this.sourceNode || node == this.targetNode) {
            console.log("Node is already contained, the sourcenode,the targetnode or not available! --FINDPATHSIMPLE-DGBUILDER");
            return;
        }
        this.mandatoryNodes.push(node);
    }

    getMandatoryNodes(): Node[] {
        for (let i = this.mandatoryNodes.length - 1; i >= 0; i--) {
            if (!this.nodes.includes(this.mandatoryNodes[i]) ||
                this.mandatoryNodes[i] == this.selectedNode ||
                this.mandatoryNodes[i] == this.targetNode) {

                this.mandatoryNodes.splice(i, 1);
            }
        }

        return this.mandatoryNodes;
    }

    removeMandatoryNode(node: Node) {
        if (this.mandatoryNodes.includes(node)) {
            this.mandatoryNodes.splice(this.mandatoryNodes.indexOf(node, 0), 1);
            node.setDefaultColor();
        }
    }

    protected leftClick(row: number, col: number, node: Node, e: MouseEvent) {
        if (this.selectSourceMode) {
            if (node != null) {
                if (this.sourceNode != null) {
                    this.sourceNode.setDefaultColor();
                }
                this.resetModes();

                this.setSourceNode(node.getId());
            }
        }
        else if (this.selectTargetMode) {
            if (node != null) {
                if (this.targetNode != null) {
                    this.targetNode.setDefaultColor();
                }
                this.resetModes();

                this.targetNode = node;
                this.targetNode.setColor(this.targetNodeColor);
            }
        }
        else if (this.selectMandatoryMode) {
            if (node != null) {
                if (this.mandatoryNodes.includes(node)) {
                    this.removeMandatoryNode(node);
                }
                else {
                    this.addMandatoryNode(node);
                }
                this.resetModes();
            }
        }
        else {
            super.leftClick(row, col, node, e);
        }
        if (!(e.ctrlKey || e.shiftKey)) {
            if (!this.bezierMode) {
                this.setNodesAndEdgesToDefault();
            }
        }
        this.cacheInvalidPaths = true;
        this.canvas.style.cursor = 'default';
    }

    protected doubleClick(row: number, col: number, node: Node, e: MouseEvent) {
        if (this.bezierMode)
            return;
        if (node == null)
            this.addNode(row, col, null, null);
        else
            if (!e.ctrlKey && !e.shiftKey && !this.onDrag) {
                if (node == this.sourceNode) {
                    this.sourceNode == null;
                }
                if (node == this.targetNode) {
                    this.targetNode == null;
                }
                this.deleteNode(node);
            }
        this.cacheInvalidPaths = true;
    }

    update() {
        if (this.targetNode != null) {
            this.targetNode.setColor(this.targetNodeColor);
        }
        if (this.sourceNode != null) {
            this.sourceNode.setColor(this.sourceNodeColor);
        }

        if (this.mandatoryNodes != null) {
            this.mandatoryNodes.forEach(mandatory => {
                mandatory.setColor(this.mandatoryNodeColor);
            })
        }


        if (this.cacheInvalidPaths) {
            this.cachedAllPaths = this.computeAllPaths();
            this.cacheInvalidPaths = false;
        }

        if (this.selectedNode != null) {
            this.selectedNode.setColor(this.selectedNodeColor);
        }

        //  console.log("cacheinvalid");
        super.update();
    }


    setNodesAndEdgesToDefault() {
        this.nodes.forEach(node => {
            if (!(node === this.targetNode || node === this.targetNode))
                node.setDefaultColor();
        })

        this.edges.forEach(edge => {
            edge.setToDefault();
        })


    }

    setTargetNode(id: number) {
        if (this.targetNode != null) {
            this.targetNode.setDefaultColor();
            this.targetNode = null;
        }
        this.nodes.forEach(node => {
            if (node.getId() == id) {
                this.targetNode = node;
                this.targetNode.setColor(this.targetNodeColor);
            }
        })

    }

    setSourceNode(id: number) {
        if (this.sourceNode != null) {
            this.sourceNode.setDefaultColor();
            this.sourceNode = null;
        }
        this.nodes.forEach(node => {
            if (node.getId() == id) {
                this.sourceNode = node;
                this.sourceNode.setColor(this.sourceNodeColor);
            }
        })
    }

    setMandatoryNode(id: number) {
        this.nodes.forEach(node => {
            if (node.getId() == id) {
                this.mandatoryNodes.push(node);
            }
        })

    }

    resetModes() {
        this.selectSourceMode = false;
        this.selectTargetMode = false;
        this.selectMandatoryMode = false;
        this.canvas.style.cursor = 'default';

    }

    selectSourceTarget() {
        this.resetModes();
        this.selectSourceMode = true;
    }

    selectTargetNode() {
        this.resetModes();
        this.selectTargetMode = true;
    }

    selectMandatoryNode() {
        this.resetModes();
        this.selectMandatoryMode = true;
    }

    getSourceNode(): number {
        return this.sourceNode.getId();
    }

    getTargetNode(): number {
        return this.targetNode.getId();
    }

    getStatus(): [boolean, string] {
        var errorMessage: string = "";
        var complete: boolean = true;

        if (this.sourceNode == null) {
            errorMessage += "You need to select a sourceNode!";
            complete = false;
        }

        if (this.targetNode == null) {
            errorMessage += "You need to select a targetNode!";
            complete = false;
        }

        if (this.sourceNode != null && this.targetNode != null) {
            var visited: Edge[] = [];
            if (!GraphAlgorithm.depthFirstSearch(this.sourceNode, this.targetNode)) {
                errorMessage += "There's no path between source and targetnode!!";
                complete = false;
            }
        }

        return [complete, errorMessage];
    }





    edgesToString(edges: Edge[]) {
        this.edges.forEach(edge => {
            console.log(edge.toString());
        });
    }

}
